import { SociometryState, RawAnswer, PreparedSociometryTest } from "shared/types/sociometry";

export const SOCIOMETRY_LOAD_TEST = "SOCIOMETRY/LOAD_TEST";
export const SOCIOMETRY_LOAD_TEST_SUCCESS = "SOCIOMETRY/LOAD_TEST_SUCCESS";
export const SOCIOMETRY_LOAD_TEST_FAILURE = "SOCIOMETRY/LOAD_TEST_FAILURE";
export const SOCIOMETRY_SELECT_ANSWER = "SOCIOMETRY/SELECT_ANSWER";
export const SOCIOMETRY_NEXT_QUESTION = "SOCIOMETRY/NEXT_QUESTION";
export const SOCIOMETRY_PREV_QUESTION = "SOCIOMETRY/PREV_QUESTION";
export const SOCIOMETRY_SUBMIT_START = "SOCIOMETRY/SUBMIT_START";
export const SOCIOMETRY_SUBMIT_SUCCESS = "SOCIOMETRY/SUBMIT_SUCCESS";
export const SOCIOMETRY_SUBMIT_FAILURE = "SOCIOMETRY/SUBMIT_FAILURE";
export const SOCIOMETRY_RESET = "SOCIOMETRY/RESET";

interface LoadTestAction {
    type: typeof SOCIOMETRY_LOAD_TEST;
}

interface LoadTestSuccessAction {
    type: typeof SOCIOMETRY_LOAD_TEST_SUCCESS;
    payload: PreparedSociometryTest;
}

interface LoadTestFailureAction {
    type: typeof SOCIOMETRY_LOAD_TEST_FAILURE;
    payload: string;
}

interface SelectAnswerAction {
    type: typeof SOCIOMETRY_SELECT_ANSWER;
    payload: RawAnswer;
}

interface NextQuestionAction {
    type: typeof SOCIOMETRY_NEXT_QUESTION;
}

interface PrevQuestionAction {
    type: typeof SOCIOMETRY_PREV_QUESTION;
}

interface SubmitStartAction {
    type: typeof SOCIOMETRY_SUBMIT_START;
}

interface SubmitSuccessAction {
    type: typeof SOCIOMETRY_SUBMIT_SUCCESS;
}

interface SubmitFailureAction {
    type: typeof SOCIOMETRY_SUBMIT_FAILURE;
    payload: string;
}

interface ResetAction {
    type: typeof SOCIOMETRY_RESET;
}

export type SociometryActionTypes =
    | LoadTestAction
    | LoadTestSuccessAction
    | LoadTestFailureAction
    | SelectAnswerAction
    | NextQuestionAction
    | PrevQuestionAction
    | SubmitStartAction
    | SubmitSuccessAction
    | SubmitFailureAction
    | ResetAction;

const initialState: SociometryState = {
    sessionId: null,
    questions: [],
    classmates: [],
    classInfo: null,
    answers: [],
    currentQuestionIndex: 0,
    loading: false,
    error: null,
    submitting: false,
};

export const sociometryReducer = (
    state = initialState,
    action: SociometryActionTypes
): SociometryState => {
    switch (action.type) {
        case SOCIOMETRY_LOAD_TEST:
            return {
                ...state,
                loading: true,
                error: null,
            };

        case SOCIOMETRY_LOAD_TEST_SUCCESS:
            return {
                ...state,
                loading: false,
                sessionId: action.payload.sessionId,
                questions: action.payload.questions,
                classmates: action.payload.classmates,
                classInfo: action.payload.classInfo,
                answers: action.payload.questions.map((q) => ({
                    questionIndex: q.questionIndex,
                    isPositive: q.isPositive,
                    chosen: [],
                })),
                currentQuestionIndex: 0,
                error: null,
            };

        case SOCIOMETRY_LOAD_TEST_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload,
            };

        case SOCIOMETRY_SELECT_ANSWER: {
            const { questionIndex, chosen } = action.payload;
            const updatedAnswers = state.answers.map((answer) =>
                answer.questionIndex === questionIndex
                    ? { ...answer, chosen }
                    : answer
            );
            return {
                ...state,
                answers: updatedAnswers,
            };
        }

        case SOCIOMETRY_NEXT_QUESTION:
            return {
                ...state,
                currentQuestionIndex: Math.min(
                    state.currentQuestionIndex + 1,
                    state.questions.length - 1
                ),
            };

        case SOCIOMETRY_PREV_QUESTION:
            return {
                ...state,
                currentQuestionIndex: Math.max(state.currentQuestionIndex - 1, 0),
            };

        case SOCIOMETRY_SUBMIT_START:
            return {
                ...state,
                submitting: true,
                error: null,
            };

        case SOCIOMETRY_SUBMIT_SUCCESS:
            return {
                ...state,
                submitting: false,
            };

        case SOCIOMETRY_SUBMIT_FAILURE:
            return {
                ...state,
                submitting: false,
                error: action.payload,
            };

        case SOCIOMETRY_RESET:
            return initialState;

        default:
            return state;
    }
};
